import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Status } from '@essent/common';
import { KEY_PRESS_DEBOUNCE_TIME } from '@innogy/become-a-customer/shared/testing';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, type Action } from '@ngrx/store';
import {
  selectLicensePlateLookupState,
  smartChargingApiActions,
} from '@smart-charging/store';
import { format } from 'date-fns';
import { MarkAsSubmittedAction, SetValueAction } from 'ngrx-forms';
import { debounceTime, filter, switchMap } from 'rxjs';

import { leadEmailDate } from '../order/order-form-submit.effects';
import {
  activateSmartChargingLeadStep,
  sendSmartChargingLead,
} from './smart-charging-lead.actions';
import {
  LICENSE_PLATE_CHECK_FORM_ID,
  licensePlateControlId,
  SmartChargingLeadWidgetStep,
} from './smart-charging-lead.reducer';
import {
  getSalesFunnelVM,
  getSmartChargingLeadLicensePlateCheckFormState,
} from './smart-charging-lead.selectors';

@Injectable()
export class SmartChargingLeadEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly http = inject(HttpClient);

  private readonly salesFunnelVM$ = this.store$.select(getSalesFunnelVM);
  private readonly licensePlateFormState$ = this.store$.select(
    getSmartChargingLeadLicensePlateCheckFormState
  );
  private readonly licensePlateLookupState$ = this.store$.select(
    selectLicensePlateLookupState
  );

  public readonly checkLicensePlate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetValueAction.TYPE),
      concatLatestFrom(() => [this.licensePlateFormState$]),
      filter(
        ([{ controlId }, formState]) =>
          controlId === licensePlateControlId &&
          formState.controls.licensePlate.isValid
      ),
      debounceTime(KEY_PRESS_DEBOUNCE_TIME),
      switchMap(([{ value: licensePlate }]) => [
        smartChargingApiActions.getLookupV1.requestAction({
          payload: {
            licensePlate,
          },
        }),
      ])
    )
  );

  public readonly submitLicensePlateCheckForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarkAsSubmittedAction.TYPE),
      filter(({ controlId }) => controlId === LICENSE_PLATE_CHECK_FORM_ID),
      concatLatestFrom(() => [
        this.licensePlateFormState$,
        this.licensePlateLookupState$,
      ]),
      switchMap(([, formState, { status: lookupStatus, data: lookupData }]) => {
        const actions: Action[] = [];

        if (formState.isInvalid || lookupStatus !== Status.SUCCESS) {
          return actions;
        }

        if (lookupData?.support_status === 'supported') {
          actions.push(
            activateSmartChargingLeadStep({
              step: SmartChargingLeadWidgetStep.LicensePlateAccepted,
            })
          );
        } else if (lookupData?.support_status === 'not_supported') {
          actions.push(
            activateSmartChargingLeadStep({
              step: SmartChargingLeadWidgetStep.LicensePlateRejected,
            })
          );
        } else if (lookupData?.support_status === 'unknown') {
          actions.push(
            activateSmartChargingLeadStep({
              step: SmartChargingLeadWidgetStep.LicensePlateUnknown,
            })
          );
        }
        return actions;
      })
    )
  );

  public readonly sendSmartChargingLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendSmartChargingLead),
      concatLatestFrom(() => [
        this.licensePlateFormState$,
        this.salesFunnelVM$,
      ]),
      switchMap(([_, , salesFunnelVM]) => {
        // The email will be send out a week after the delivery has started, taking into account the cooldown period
        const emailDate = leadEmailDate(salesFunnelVM.selectedStartDate);

        // In scope for refactor with https://jira.essent.nl/jira/browse/CTR-2190
        this.http
          .post('/api/thuisvoordeel-interest-trigger', {
            parameter_values: {
              customerId: '',
              email: salesFunnelVM.email,
              gender: salesFunnelVM.gender,
              initials: salesFunnelVM.initials,
              prefix: salesFunnelVM.prefix ?? '',
              lastName: salesFunnelVM.lastName,
              MAILNAME_FULL: 'rc_20251103_ess_feh_0271_acq_slim_laden',
              UITING: 'ess_feh_0271',
              INSTANT: 'false',
              SENDOUT_DT: format(emailDate, 'yyyy-MM-dd HH:mm'),
              DESCRIPTION_1: 'Startdatum contract',
              VALUE_1: format(
                new Date(salesFunnelVM.selectedStartDate),
                'yyyy-MM-dd'
              ),
              DESCRIPTION_2: '',
              VALUE_2: '',
              DESCRIPTION_3: '',
              VALUE_3: '',
              DESCRIPTION_4: '',
              VALUE_4: '',
              DESCRIPTION_5: '',
              VALUE_5: '',
              DESCRIPTION_6: '',
              VALUE_6: '',
            },
          })
          .subscribe();

        return [
          activateSmartChargingLeadStep({
            step: SmartChargingLeadWidgetStep.ThankYouScreen,
          }),
        ];
      })
    )
  );
}
